import React, { Suspense, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Card, { CardBody } from '../../components/bootstrap/Card';
import PageWrapper from '../PageWrapper/PageWrapper';
import Page from '../Page/Page';
import SubHeader from '../SubHeader/SubHeader';
import ContentRoutes from './ContentRoutes';

const LOADING = (
	<PageWrapper>
		<SubHeader>
			<div />
		</SubHeader>
		<Page>
			<div className='row h-100'>
				<div className='col-lg-6'>
					<Card stretch>
						<CardBody>
							<div />
						</CardBody>
					</Card>
				</div>
				<div className='col-lg-6'>
					<Card stretch='semi'>
						<CardBody>
							<div />
						</CardBody>
					</Card>
					<Card stretch='semi'>
						<CardBody>
							<div />
						</CardBody>
					</Card>
				</div>
			</div>
		</Page>
	</PageWrapper>
);

const REDUCED_MOTION =
	typeof window !== 'undefined' &&
	window.matchMedia &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Content = () => {
	const location = useLocation();
	const mainRef = useRef<HTMLElement>(null);

	// One-time arrival animation when landing from the login page.
	useEffect(() => {
		if (REDUCED_MOTION) return undefined;
		let timer: number | undefined;
		try {
			if (sessionStorage.getItem('phoenix_just_logged_in') === '1') {
				sessionStorage.removeItem('phoenix_just_logged_in');
				document.body.classList.add('phx-arriving');
				timer = window.setTimeout(
					() => document.body.classList.remove('phx-arriving'),
					1300,
				);
			}
		} catch (_) {
			/* private mode */
		}
		return () => {
			if (timer) window.clearTimeout(timer);
			document.body.classList.remove('phx-arriving');
		};
	}, []);

	// Ambient parallax — smoothed mouse position feeds --phx-mx / --phx-my,
	// consumed by the orb + page-head CSS in phoenix-design.scss.
	useEffect(() => {
		if (REDUCED_MOTION) return undefined;
		const el = mainRef.current;
		if (!el) return undefined;
		let targetX = 0;
		let targetY = 0;
		let x = 0;
		let y = 0;
		let raf = 0;
		const tick = () => {
			x += (targetX - x) * 0.06;
			y += (targetY - y) * 0.06;
			el.style.setProperty('--phx-mx', x.toFixed(3));
			el.style.setProperty('--phx-my', y.toFixed(3));
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		const onMove = (e: MouseEvent) => {
			const cx = window.innerWidth / 2;
			const cy = window.innerHeight / 2;
			targetX = (e.clientX - cx) / cx;
			targetY = (e.clientY - cy) / cy;
		};
		window.addEventListener('mousemove', onMove);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('mousemove', onMove);
		};
	}, []);

	return (
		<main className='content phx-content' ref={mainRef}>
			<Suspense fallback={LOADING}>
				{/* key on pathname re-mounts the wrapper so the page-in
				    animation replays on every navigation */}
				<div className='phx-page-anim' key={location.pathname}>
					<ContentRoutes />
				</div>
			</Suspense>
		</main>
	);
};

export default Content;
